import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Flame, UserPlus, Share2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { shareInvite } from "../lib/share";

/**
 * ShareCard — lightweight invite / growth loop. Condensed to two
 * self-explanatory buttons (Share my streak / Challenge a friend); no
 * title or blurb so it stays small on the phone home screen.
 *
 * Props:
 *   compact: renders a single share button (used on the Profile page)
 */
export default function ShareCard({ compact = false }) {
  const { t } = useTranslation();
  const { profile } = useAuth();

  const streak = Math.max(
    Number(profile?.current_streak || 0),
    Number(profile?.daily_challenge_streak || 0),
  );
  const calories = Math.round(Number(profile?.calories_burned || 0));

  const doShare = async (kind) => {
    const text =
      kind === "streak"
        ? t("share.streak_text", { streak, calories })
        : t("share.challenge_text");
    const res = await shareInvite({ title: t("share.dialog_title"), text });
    if (res === "copied") toast.success(t("share.copied"));
    else if (res === "failed") toast.error(t("share.failed"));
  };

  if (compact) {
    return (
      <Button
        data-testid="profile-share-btn"
        onClick={() => doShare("challenge")}
        variant="outline"
        className="w-full h-12 rounded-2xl border-[#E6E2D8] text-[#2C332A] hover:bg-[#F7F5F0]"
      >
        <Share2 size={16} strokeWidth={1.6} className="mr-2 text-[#5F7161]" />
        {t("share.profile_btn")}
      </Button>
    );
  }

  return (
    <div data-testid="share-card" className="flex flex-col gap-2.5">
      <Button
        data-testid="share-streak-btn"
        onClick={() => doShare("streak")}
        className="h-11 w-full rounded-2xl bg-[#5F7161] hover:bg-[#4D5D4F] text-white font-medium text-sm justify-center"
      >
        <Flame size={16} strokeWidth={1.8} className="mr-2 shrink-0" />
        {t("share.streak_btn")}
      </Button>
      <Button
        data-testid="share-challenge-btn"
        onClick={() => doShare("challenge")}
        variant="outline"
        className="h-11 w-full rounded-2xl border-[#E6E2D8] bg-white hover:bg-[#F7F5F0] text-[#2C332A] font-medium text-sm justify-center"
      >
        <UserPlus size={16} strokeWidth={1.8} className="mr-2 shrink-0 text-[#5F7161]" />
        {t("share.challenge_btn")}
      </Button>
    </div>
  );
}
